import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../../components/ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { Plus, Pencil, Trash2, Loader2, Search, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../../components/ui/checkbox';

const TeachersPage = () => {
  const { user } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [assigningTeacher, setAssigningTeacher] = useState(null);
  const [allClasses, setAllClasses] = useState([]);
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [saving, setSaving] = useState(false);
  const [assigningLoading, setAssigningLoading] = useState(false);

  useEffect(() => {
    fetchTeachers();
    fetchAllClasses();
  }, []);

  const fetchAllClasses = async () => {
    try {
      const res = await api.get('/classes');
      setAllClasses(res.data);
    } catch (err) {
      console.error('Failed to load classes:', err);
    }
  };

  const fetchTeachers = async () => {
    try {
      const res = await api.get('/users?role=teacher');
      const teachersData = res.data;
      
      // Fetch assigned classes for each teacher
      const teachersWithClasses = await Promise.all(
        teachersData.map(async (teacher) => {
          try {
            const classesRes = await api.get(`/teachers/${teacher.id}/classes`);
            return { ...teacher, assigned_classes: classesRes.data };
          } catch (err) {
            return { ...teacher, assigned_classes: [] };
          }
        })
      );
      
      setTeachers(teachersWithClasses);
    } catch (err) {
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || (!editingTeacher && !formData.password)) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingTeacher) {
        await api.put(`/users/${editingTeacher.id}`, formData);
        toast.success('Teacher updated successfully');
      } else {
        await api.post('/users', { ...formData, role: 'teacher' });
        toast.success('Teacher created successfully');
      }
      setDialogOpen(false);
      setEditingTeacher(null);
      setFormData({ name: '', email: '', password: '' });
      fetchTeachers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      name: teacher.name,
      email: teacher.email,
      password: ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (teacherId) => {
    if (!window.confirm('Are you sure you want to delete this teacher?')) return;
    
    try {
      await api.delete(`/users/${teacherId}`);
      toast.success('Teacher deleted successfully');
      fetchTeachers();
    } catch (err) {
      toast.error('Failed to delete teacher');
    }
  };

  const handleAssignClasses = async (teacher) => {
    setAssigningTeacher(teacher);
    setAssigningLoading(true);
    setAssignDialogOpen(true);
    
    try {
      // Fetch teacher's current classes
      const res = await api.get(`/teachers/${teacher.id}/classes`);
      setTeacherClasses(res.data);
      setSelectedClasses(res.data.map(c => c.id));
    } catch (err) {
      console.error('Failed to load teacher classes:', err);
      setTeacherClasses([]);
      setSelectedClasses([]);
    } finally {
      setAssigningLoading(false);
    }
  };

  const handleSaveAssignments = async () => {
    if (!assigningTeacher) return;
    
    setSaving(true);
    try {
      await api.post(`/teachers/${assigningTeacher.id}/assign-classes`, {
        section_ids: selectedClasses  // Field name for backend compatibility
      });
      toast.success('Classes assigned successfully');
      setAssignDialogOpen(false);
      setAssigningTeacher(null);
      setSelectedClasses([]);
      // Refresh both teachers and classes to keep data in sync
      fetchTeachers();
      fetchAllClasses();
    } catch (err) {
      // Handle error - detail might be a string or an object
      let errorMessage = 'Failed to assign classes';
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          // Pydantic validation errors are arrays
          errorMessage = err.response.data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof err.response.data.detail === 'object') {
          errorMessage = err.response.data.detail.msg || err.response.data.detail.message || JSON.stringify(err.response.data.detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const toggleClass = (classId) => {
    setSelectedClasses(prev => 
      prev.includes(classId) 
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  const filteredTeachers = teachers.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Teachers</h1>
            <p className="text-slate-500">Manage teacher accounts</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingTeacher(null);
              setFormData({ name: '', email: '', password: '' });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-teacher-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Teacher
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter teacher name"
                    data-testid="teacher-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                    data-testid="teacher-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">
                    Password {editingTeacher ? '(leave blank to keep current)' : '*'}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter password"
                    data-testid="teacher-password-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="save-teacher-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {editingTeacher ? 'Update' : 'Create'} Teacher
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search teachers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-teachers-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              </div>
            ) : filteredTeachers.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No teachers found</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Assigned Classes</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTeachers.map((teacher) => (
                      <TableRow key={teacher.id}>
                        <TableCell className="font-medium">{teacher.name}</TableCell>
                        <TableCell>{teacher.email}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {(teacher.assigned_classes || []).length === 0 ? (
                              <span className="text-slate-400 text-sm">None</span>
                            ) : (teacher.assigned_classes || []).length <= 5 ? (
                              (teacher.assigned_classes || []).map(cls => (
                                <span 
                                  key={cls.id} 
                                  className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                                >
                                  {cls.name}
                                </span>
                              ))
                            ) : (
                              <span className="text-sm text-indigo-600 dark:text-indigo-400">
                                {(teacher.assigned_classes || []).length} classes
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {teacher.created_at ? new Date(teacher.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleAssignClasses(teacher)}
                              title="Assign Classes"
                              data-testid={`assign-classes-${teacher.id}`}
                            >
                              <BookOpen className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(teacher)}
                              data-testid={`edit-teacher-${teacher.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(teacher.id)}
                              className="text-rose-600 hover:text-rose-700"
                              data-testid={`delete-teacher-${teacher.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assign Classes Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={(open) => {
          setAssignDialogOpen(open);
          if (!open) {
            setAssigningTeacher(null);
            setSelectedClasses([]);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Assign Classes to {assigningTeacher?.name}
              </DialogTitle>
            </DialogHeader>
            
            {assigningLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Select the classes this teacher will be responsible for. Teachers can only access students and data from their assigned classes.
                </p>
                
                {allClasses.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4">No classes available. Please create classes first.</p>
                ) : (
                  <div className="border rounded-lg p-4">
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 max-h-[400px] overflow-y-auto">
                      {allClasses.map(classItem => (
                        <div key={classItem.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`class-${classItem.id}`}
                            checked={selectedClasses.includes(classItem.id)}
                            onCheckedChange={() => toggleClass(classItem.id)}
                          />
                          <label
                            htmlFor={`class-${classItem.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {classItem.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <p className="text-sm text-slate-600 mb-2">
                    <strong>Selected:</strong> {selectedClasses.length} class(es)
                  </p>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setAssignDialogOpen(false)}
                disabled={assigningLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAssignments}
                disabled={assigningLoading}
                data-testid="save-assignments-btn"
              >
                {assigningLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Assignments
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TeachersPage;
